import React, { useMemo } from "react"
import { Button } from "antd"
import {
	WarningOutlined,
	HomeOutlined,
	InboxOutlined,
	PlusOutlined,
	MinusOutlined,
	SwapOutlined,
	DragOutlined,
	LinkOutlined,
	ColumnWidthOutlined,
} from "@ant-design/icons"
import { DndProvider } from "react-dnd"
import { HTML5Backend } from "react-dnd-html5-backend"
import Strings from "utils/strings"
import { Route } from "../../types"
import { MovePreviewState } from "./types"
import { useMoveOrdersState } from "./useMoveOrdersState"
import { useTimelineLayout } from "./useTimelineLayout"
import RouteRow from "./RouteRow"
import OptimizePreviewModal from "./OptimizePreviewModal"
import {
	COMPACT_ZOOM_THRESHOLD,
	GROUPED_COLOR,
	MAX_ZOOM,
	MIN_ZOOM,
	MOVED_COLOR,
	REPOSITIONED_COLOR,
	SIDEBAR_WIDTH,
	TICKS_RULER_HEIGHT,
	ZOOM_BUTTON_FACTOR,
	PICKUP_COLOR,
	formatClock,
	tickStepMinutes,
	toInsetPercent,
} from "./utils"

const BLUE = REPOSITIONED_COLOR
const AMBER = "#b45309"

interface MoveOrdersTimelineProps {
	selectedRoutes: Route[]
	// Full fetched list — needed to look up a bike route auto-updated by
	// cascade even when it was never itself selected.
	allRoutes: Route[]
	onSuccess: () => void
	onPreviewChange?: (state: MovePreviewState) => void
}

const MoveOrdersTimeline: React.FC<MoveOrdersTimelineProps> = ({
	selectedRoutes,
	allRoutes,
	onSuccess,
	onPreviewChange,
}) => {
	const {
		stopsByRoute,
		pendingMoves,
		positionOverrides,
		sequenceByRoute,
		changedCount,
		applying,
		previewLoading,
		confirmingOptimize,
		optimizePreview,
		timeDomain,
		routeColor,
		selectedOrderId,
		toggleSelectOrder,
		handleDrop,
		handleDiscard,
		handleAdjustEtas,
		handlePreviewOptimize,
		handleCancelOptimizePreview,
		handleConfirmOptimize,
	} = useMoveOrdersState(selectedRoutes, onSuccess, onPreviewChange)

	const {
		height,
		zoom,
		zoomBy,
		zoomToFit,
		scrollRef,
		measureRef,
		trackWidth,
		isPanning,
	} = useTimelineLayout(selectedRoutes.length)
	const compact = zoom < COMPACT_ZOOM_THRESHOLD

	// Baseline from the zoom level, escalated for pixel width — see tickStepMinutes.
	const ticks = useMemo(() => {
		if (!timeDomain) return []
		const totalMinutes = timeDomain.range / 60
		const stepMinutes = tickStepMinutes(zoom, trackWidth / totalMinutes)
		const count = Math.floor(totalMinutes / stepMinutes)

		return Array.from({ length: count + 1 }, (_, i) => {
			const timestamp = timeDomain.start + i * stepMinutes * 60
			return {
				percent: toInsetPercent(
					((timestamp - timeDomain.start) / timeDomain.range) * 100,
					trackWidth
				),
				label: formatClock(timestamp),
			}
		})
	}, [timeDomain, trackWidth, zoom])

	return (
		<div className="RoutesScreen__timeline" style={{ height }}>
			<div className="RoutesScreen__timeline-header" ref={measureRef}>
				<div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
					<span style={{ fontSize: 13, fontWeight: 600 }}>
						{Strings.routes.moveOrdersTitle}
					</span>
					<span style={{ fontSize: 11, color: "#bfbfbf" }}>
						{selectedRoutes.length === 1
							? Strings.routes.moveOrdersHintSingleRoute
							: Strings.routes.moveOrdersHint}
					</span>
				</div>
				<div style={{ display: "flex", alignItems: "center", gap: 12 }}>
					{changedCount > 0 && (
						<span
							style={{
								fontSize: 12.5,
								fontWeight: 600,
								color: AMBER,
								background: "#fef3e2",
								border: "1px solid #f6dcae",
								borderRadius: 6,
								padding: "4px 10px",
								whiteSpace: "nowrap",
							}}
						>
							{changedCount === 1
								? Strings.routes.moveOrdersPendingCountOne
								: Strings.formatString(
										Strings.routes.moveOrdersPendingCountMany,
										changedCount
									)}
						</span>
					)}
					<Button
						size="small"
						disabled={changedCount === 0}
						onClick={handleDiscard}
						style={{ borderRadius: 7 }}
					>
						{Strings.routes.moveOrdersDiscard}
					</Button>
					<Button
						size="small"
						disabled={changedCount === 0}
						loading={previewLoading}
						onClick={handlePreviewOptimize}
						style={{
							borderRadius: 7,
							color: changedCount === 0 ? undefined : BLUE,
							borderColor: changedCount === 0 ? undefined : BLUE,
						}}
					>
						{Strings.routes.moveOrdersOptimize}
					</Button>
					<Button
						size="small"
						type="primary"
						disabled={changedCount === 0}
						loading={applying}
						onClick={handleAdjustEtas}
						style={{ borderRadius: 7 }}
					>
						{Strings.routes.moveOrdersAdjustEtas}
					</Button>
				</div>
			</div>

			<DndProvider backend={HTML5Backend}>
				<div
					ref={scrollRef}
					style={{
						overflow: "auto",
						flex: 1,
						minHeight: 0,
						cursor: "move",
						userSelect: isPanning ? "none" : undefined,
					}}
				>
					<div
						style={{ width: SIDEBAR_WIDTH + trackWidth, position: "relative" }}
					>
						<div style={{ display: "flex" }}>
							<div
								style={{
									width: SIDEBAR_WIDTH,
									flexShrink: 0,
									position: "sticky",
									left: 0,
									background: "#fff",
									zIndex: 3,
								}}
							/>
							<div
								style={{
									width: trackWidth,
									flexShrink: 0,
									position: "relative",
									height: TICKS_RULER_HEIGHT,
								}}
							>
								{ticks.map((tick) => (
									<span
										key={tick.label + tick.percent}
										style={{
											position: "absolute",
											left: `${tick.percent}%`,
											transform: "translateX(-50%)",
											fontSize: 11.5,
											fontWeight: 500,
											color: "#9aa0a8",
										}}
									>
										{tick.label}
									</span>
								))}
							</div>
						</div>

						{/* Faint vertical guides down through every row, so a stop's
							position can be read against the ruler even several routes
							down, not just by eyeballing across the row it's actually in. */}
						<div
							style={{
								position: "absolute",
								left: SIDEBAR_WIDTH,
								top: TICKS_RULER_HEIGHT,
								bottom: 0,
								width: trackWidth,
								pointerEvents: "none",
							}}
						>
							{ticks.map((tick) => (
								<div
									key={`grid-${tick.label}-${tick.percent}`}
									style={{
										position: "absolute",
										left: `${tick.percent}%`,
										top: 0,
										bottom: 0,
										width: 1,
										background: "#f1f2f4",
									}}
								/>
							))}
						</div>

						{selectedRoutes.map((route) => (
							<RouteRow
								key={route._id}
								route={route}
								color={routeColor(route._id)}
								stops={stopsByRoute[route._id] || []}
								timeDomain={timeDomain}
								trackWidth={trackWidth}
								positionOverrides={positionOverrides}
								pendingMoves={pendingMoves}
								sequenceByOrderId={sequenceByRoute[route._id] || {}}
								onDropOrder={handleDrop}
								selectedOrderId={selectedOrderId}
								onSelectOrder={toggleSelectOrder}
								compact={compact}
							/>
						))}
					</div>
				</div>
			</DndProvider>

			<div className="RoutesScreen__timeline-legend">
				<div className="RoutesScreen__timeline-legend-items">
					<span>
						<HomeOutlined /> {Strings.routes.timelineLegendDepot}
					</span>
					<span>
						<span className="RoutesScreen__timeline-legend-dot" />
						{Strings.routes.timelineLegendStop}
					</span>
					<span>
						<InboxOutlined style={{ color: PICKUP_COLOR }} />{" "}
						{Strings.routes.bikePickup}
					</span>
					<span>
						<span
							style={{
								display: "inline-flex",
								alignItems: "center",
								justifyContent: "center",
								width: 14,
								height: 14,
								borderRadius: "50%",
								background: MOVED_COLOR,
							}}
						>
							<SwapOutlined style={{ color: "#fff", fontSize: 8 }} />
						</span>
						{Strings.routes.timelineLegendMoved}
					</span>
					<span>
						<span
							style={{
								display: "inline-flex",
								alignItems: "center",
								justifyContent: "center",
								width: 14,
								height: 14,
								borderRadius: "50%",
								background: REPOSITIONED_COLOR,
							}}
						>
							<DragOutlined style={{ color: "#fff", fontSize: 8 }} />
						</span>
						{Strings.routes.timelineLegendRepositioned}
					</span>
					<span>
						<span
							style={{
								display: "inline-flex",
								alignItems: "center",
								justifyContent: "center",
								width: 14,
								height: 14,
								borderRadius: "50%",
								background: "#eab308",
							}}
						>
							<WarningOutlined style={{ color: "#fff", fontSize: 8 }} />
						</span>
						{Strings.routes.timelineLegendOutOfFormat}
					</span>
					<span>
						<span
							style={{
								display: "inline-flex",
								alignItems: "center",
								justifyContent: "center",
								width: 14,
								height: 14,
								borderRadius: "50%",
								background: GROUPED_COLOR,
							}}
						>
							<LinkOutlined style={{ color: "#fff", fontSize: 8 }} />
						</span>
						{Strings.routes.timelineLegendBundled}
					</span>
				</div>
				<div className="RoutesScreen__timeline-zoom">
					<span>{Strings.routes.timelineZoom}</span>
					<Button
						size="small"
						icon={<MinusOutlined />}
						disabled={zoom <= MIN_ZOOM}
						onClick={() => zoomBy(1 / ZOOM_BUTTON_FACTOR)}
						style={{ borderRadius: 6 }}
					/>
					<Button
						size="small"
						icon={<PlusOutlined />}
						disabled={zoom >= MAX_ZOOM}
						onClick={() => zoomBy(ZOOM_BUTTON_FACTOR)}
						style={{ borderRadius: 6 }}
					/>
					<Button
						size="small"
						icon={<ColumnWidthOutlined />}
						disabled={zoom === MIN_ZOOM}
						onClick={zoomToFit}
						title={Strings.routes.timelineZoomFit}
						style={{ borderRadius: 6 }}
					/>
				</div>
			</div>

			<OptimizePreviewModal
				selectedRoutes={selectedRoutes}
				allRoutes={allRoutes}
				stopsByRoute={stopsByRoute}
				sequenceByRoute={sequenceByRoute}
				pendingMoves={pendingMoves}
				routeColor={routeColor}
				optimizePreview={optimizePreview}
				confirming={confirmingOptimize}
				onCancel={handleCancelOptimizePreview}
				onConfirm={handleConfirmOptimize}
			/>
		</div>
	)
}

export default MoveOrdersTimeline
